// Dashboard Portal
// pages/UserProfile/Profile.js
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import Select from "react-select";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, Box, Divider, Typography } from '@mui/material';

import CountrySelection from "./CountrySelection";
import axiosInstance from "../../utils/axiosInstance";

const ProfilePage = forwardRef((props, ref) => {
    const { includePasswordUpdate = false, isSetupMode = false } = props;

    useEffect(() => {
        document.title = 'Profile | KlonIT Web Application';
        fetchData();
    }, []);

    const [UserProfile, setUserProfile] = useState("");
    const [UserfName, setUserfName] = useState("");
    const [UserlName, setUserlName] = useState("");
    const [UserEmail, setUserEmail] = useState("");
    const [UserAddress, setUserAddress] = useState("");
    const [UserCountry, setUserCountry] = useState("");
    const [UserCity, setUserCity] = useState("");
    const [UserState, setUserState] = useState("");
    const [UserZip, setUserZip] = useState("");
    const [message, setMessage] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [isFormValid, setIsFormValid] = useState(false);

    // Subscription fields - updated with separate consents
    const [subscriptionEmail, setSubscriptionEmail] = useState("");
    const [subscriptionPhone, setSubscriptionPhone] = useState("");
    const [promotionalConsent, setPromotionalConsent] = useState(false);
    const [exclusiveDealsConsent, setExclusiveDealsConsent] = useState(false);
    const [termsConsent, setTermsConsent] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscriptionMessage, setSubscriptionMessage] = useState("");
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [emailMismatchWarning, setEmailMismatchWarning] = useState("");

    // Password fields for non-admin users
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordError, setPasswordError] = useState("");

    // Form validation - Check if all required profile fields are filled
    useEffect(() => {
        let isValid = UserfName !== "" &&
            UserlName !== "" &&
            UserEmail !== "" &&
            UserAddress !== "" &&
            UserCountry !== "" &&
            UserCity !== "" &&
            UserState !== "" &&
            UserZip !== "";

        // For password validation when includePasswordUpdate is true
        if (includePasswordUpdate) {
            isValid = isValid && 
                        newPassword !== "" && 
                        newPassword.length >= 8 &&
                        newPassword === confirmPassword;
        }

        setIsFormValid(isValid);
        props.onFormValidityChange && props.onFormValidityChange(isValid);
    }, [UserfName, UserlName, UserEmail, UserAddress, UserCountry, UserCity, UserState, UserZip, newPassword, confirmPassword, includePasswordUpdate, props]);


    const fetchData = () => {
        axiosInstance
            .get(`/get-user-profile`)
            .then((response) => {
                const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
                if (responseData.length > 0){
                    let profile_data = responseData[0]
                    setUserProfile(profile_data.profile_image);
                    setUserfName(profile_data.fname);
                    setUserlName(profile_data.lname);
                    setUserEmail(profile_data.email);
                    setUserAddress(profile_data.address_one);
                    setUserCountry(profile_data.country);
                    setUserCity(profile_data.city);
                    setUserState(profile_data.state);
                    setUserZip(profile_data.zip);
                    
                    // Pre-fill subscription email with user's email
                    setSubscriptionEmail(profile_data.email);
                }else{
                    const user = JSON.parse(localStorage.getItem("user"));
                    if(Object.keys(user).length > 0){
                        setUserEmail(user.user_email);
                        setSubscriptionEmail(user.user_email);
                    }
                }
            })
            .catch((error) => {
                setMessage("Error fetching for User Profile:", error);
            });
    };


    // Check if subscription email matches user's signup email
    useEffect(() => {
        if (subscriptionEmail && UserEmail && subscriptionEmail !== UserEmail) {
            setEmailMismatchWarning("⚠️ This email is different from your account email. Please use your signup email for subscription.");
        } else {
            setEmailMismatchWarning("");
        }
    }, [subscriptionEmail, UserEmail]);

    // Check if Join button should be enabled
    const isJoinEnabled = () => {
        // Must have at least email or phone
        const hasContact = subscriptionEmail || subscriptionPhone;
        
        // If email provided, must match signup email
        const emailValid = !subscriptionEmail || (subscriptionEmail === UserEmail);
        
        // Must have at least one consent type and terms consent
        const hasConsents = (promotionalConsent || exclusiveDealsConsent) && termsConsent;
        
        return hasContact && emailValid && hasConsents && !isSubscribed;
    };

    const handleCountryChange = (selectedOption) => {
        setUserCountry(selectedOption ? selectedOption.value : "");
    };

    const getDefaultCountryOption = () => {
        return CountrySelection.find(option => option.value === UserCountry);
    };

    const updateUserProfileToLocalStorage = (data) => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const userData = JSON.parse(userStr);
            userData.profile = data || null;
            localStorage.setItem('user', JSON.stringify(userData));
            window.dispatchEvent(new CustomEvent('userLocalStorageUpdated', { detail: userData  }));
            console.log("Local storage user profile updated.", data);
        }
    }

    const validatePassword = () => {
        if (!includePasswordUpdate) return true;
        
        if (newPassword || confirmPassword) {
            if (!newPassword) {
                setPasswordError("New password is required");
                return false;
            }
            if (newPassword.length < 8) {
                setPasswordError("New password must be at least 8 characters");
                return false;
            }
            if (newPassword !== confirmPassword) {
                setPasswordError("Passwords do not match");
                return false;
            }
        }
        
        setPasswordError("");
        return true;
    };

    const handlePasswordUpdate = async () => {
        if (!newPassword && !confirmPassword) {
            return true;
        }

        try {
            const response = await axiosInstance.post(`/update-password`, {
                new_password: newPassword,
                confirm_password: confirmPassword,
                is_initial_setup: isSetupMode
            });

            if (response.data.success) {
                setNewPassword("");
                setConfirmPassword("");
                return true;
            } else {
                setPasswordError(response.data.message || "Failed to update password");
                return false;
            }
        } catch (error) {
            setPasswordError(error.response?.data?.message || "Error updating password");
            return false;
        }
    };

    const handleJoinSubscription = async () => {
        if (!isJoinEnabled()) {
            return;
        }

        setIsSubscribing(true);
        setSubscriptionMessage("");

        try {
            // Send single request with both email and phone
            const response = await axiosInstance.post('/api/subscribe', {
                email: subscriptionEmail || null,
                phone: subscriptionPhone || null,
                promotional_consent: promotionalConsent,
                exclusive_deals_consent: exclusiveDealsConsent,
                terms_consent: termsConsent
            });

            if (response.data.success) {
                // Build success message based on selected consents
                const consentTypes = [];
                if (promotionalConsent) consentTypes.push("promotional offers");
                if (exclusiveDealsConsent) consentTypes.push("exclusive deals");
                
                const consentText = consentTypes.join(" and ");
                const channels = [];
                if (subscriptionEmail) channels.push("email");
                if (subscriptionPhone) channels.push("SMS");
                const channelText = channels.join(" and ");

                setSubscriptionMessage(`✓ Successfully subscribed to ${consentText} via ${channelText}!`);
                setIsSubscribed(true);
                
                // Clear form
                setSubscriptionEmail("");
                setSubscriptionPhone("");
                setPromotionalConsent(false);
                setExclusiveDealsConsent(false);
                setTermsConsent(false);
            }

        } catch (error) {
            console.error("Subscription error:", error);
            
            if (error.response?.data?.message) {
                setSubscriptionMessage(error.response.data.message);
            } else if (error.response?.status === 409) {
                setSubscriptionMessage("You are already subscribed with this email or phone number.");
            } else if (error.response?.status === 400) {
                setSubscriptionMessage("Invalid input. Please check your information.");
            } else {
                setSubscriptionMessage("An error occurred. Please try again later.");
            }
        } finally {
            setIsSubscribing(false);
        }
    };
    
    const handleSaveButtonClick = async () => {
        // Validate password if needed
        if (!validatePassword()) {
            setMessage(passwordError);
            props.onError && props.onError(passwordError);
            return;
        }

        // Update password first if provided
        if (includePasswordUpdate && (newPassword || confirmPassword)) {
            const passwordUpdated = await handlePasswordUpdate();
            if (!passwordUpdated) {
                setMessage(passwordError);
                props.onError && props.onError(passwordError);
                return;
            }
        }

        if (selectedFile) {
            // Check if the selected file is an image
            const validExtensions = ['image/jpeg', 'image/jpg', 'image/png'];
            if (!validExtensions.includes(selectedFile.type)) {
                setMessage("Invalid file type. Only JPG, JPEG, and PNG are allowed.");
                props.onError && props.onError("Invalid file type");
                return;
            }

            // Create a new FormData object to send the file to the server
            const formData = new FormData();
            formData.append('avatar', selectedFile);
            formData.append('fname', UserfName);
            formData.append('lname', UserlName);
            formData.append('address_one', UserAddress);
            formData.append('country', UserCountry);
            formData.append('city', UserCity);
            formData.append('state', UserState);
            formData.append('zip', UserZip);

            await axiosInstance.post(`/update-user-profile`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
                .then((response) => {
                    if(response.data.success){
                        const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
                        setMessage("Profile updated successfully!");
                        updateUserProfileToLocalStorage(responseData.data.profile_image);
                        props.onComplete && props.onComplete(response.data);
                    } else {
                        setMessage(response.data.message);
                        props.onError && props.onError(response.data);
                    }
                })
                .catch((error) => {
                    setMessage("Error updating profile.");
                    props.onError && props.onError(error);
                });
        } else {
            const data = {
                fname: UserfName,
                lname: UserlName,
                address_one: UserAddress,
                country: UserCountry,
                city: UserCity,
                state: UserState,
                zip: UserZip
            };

            await axiosInstance.post(`/update-user-profile`, data)
                .then((response) => {
                    if(response.data.success){
                        const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
                        updateUserProfileToLocalStorage(responseData);
                        setMessage(response.data.message);
                        
                        // For non-admins in setup mode, go directly to chatbots
                        const nextPath =  "/accept-terms";
                        props.onComplete && props.onComplete(nextPath);
                    } else {
                        setMessage(response.data.message);
                        props.onError && props.onError(response.data);
                    }
                })
                .catch((error) => {
                    setMessage("Error updating profile.");
                    props.onError && props.onError(error);
                });
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
    };


    useImperativeHandle(ref, () => ({
        handleSaveButtonClick,
    }));

    return (
        <div className="container">
            <div className="row my-3">
                <div className="col-12">
                    <div className="card mb-3 p-4">
                        <div className="row">
                            <div className="mb-3 col-md-6">
                                <label className="form-label" htmlFor="inputProfilePicture">
                                    Profile Picture
                                </label>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="inputProfilePicture"
                                    accept="image/jpeg, image/jpg, image/png"
                                    onChange={handleFileChange}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="mb-3 col-md-6">
                                <label className="form-label" htmlFor="inputFirstName">
                                    First name <small className="text-danger">*</small>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="inputFirstName"
                                    value={UserfName}
                                    onChange={(e) => setUserfName(e.target.value)}
                                />
                            </div>
                            <div className="mb-3 col-md-6">
                                <label className="form-label" htmlFor="inputLastName">
                                    Last name <small className="text-danger">*</small>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="inputLastName"
                                    value={UserlName}
                                    onChange={(e) => setUserlName(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="mb-3">
                            <label className="form-label" htmlFor="inputEmail">
                                Email <small className="text-danger">*</small>
                            </label>
                            <input
                                type="email"
                                className="form-control"
                                id="inputEmail"
                                value={UserEmail}
                                onChange={(e) => setUserEmail(e.target.value)}
                                readOnly
                                disabled
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label" htmlFor="inputAddress">
                                Address <small className="text-danger">*</small>
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="inputAddress"
                                value={UserAddress}
                                onChange={(e) => setUserAddress(e.target.value)}
                            />
                        </div>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label" htmlFor="inputCountry">
                                    Country <small className="text-danger">*</small>
                                </label>
                                <Select
                                    id="inputCountry"
                                    options={CountrySelection}
                                    value={getDefaultCountryOption()}
                                    onChange={handleCountryChange}
                                    isClearable
                                />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label" htmlFor="inputCity">
                                    City <small className="text-danger">*</small>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="inputCity"
                                    value={UserCity}
                                    onChange={(e) => setUserCity(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label" htmlFor="inputState">
                                    State <small className="text-danger">*</small>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="inputState"
                                    value={UserState}
                                    onChange={(e) => setUserState(e.target.value)}
                                />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label" htmlFor="inputZip">
                                    Zip Code <small className="text-danger">*</small>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="inputZip"
                                    value={UserZip}
                                    onChange={(e) => setUserZip(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Password Update Section */}
                        {includePasswordUpdate && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                <Typography variant="h6" gutterBottom>
                                    Update Password
                                </Typography>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label" htmlFor="newPassword">
                                            New Password <small className="text-danger">*</small>
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                className="form-control"
                                                id="newPassword"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                placeholder="Enter new password (min. 8 characters)"
                                            />
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                            >
                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </button>
                                        </div>
                                        <small className="text-muted">Minimum 8 characters</small>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="form-label" htmlFor="confirmPassword">
                                            Confirm New Password <small className="text-danger">*</small>
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                className="form-control"
                                                id="confirmPassword"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                placeholder="Confirm new password"
                                            />
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {passwordError && (
                                    <div className="alert alert-danger" role="alert">
                                        {passwordError}
                                    </div>
                                )}
                            </>
                        )}
                        {/* Subscription Section - Updated with Separate Consents */}
                        {!isSubscribed && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                <Typography variant="h6" gutterBottom>
                                    Stay Connected
                                </Typography>
                                
                                <div className="alert alert-info" role="alert">
                                    Subscribe to receive exclusive deals, product updates, and important notifications from KlonIT via email and SMS.
                                </div>

                                {/* Email Field */}
                                <div className="mb-3">
                                    <label className="form-label" htmlFor="subscriptionEmail">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        className={`form-control ${emailMismatchWarning ? 'is-invalid' : ''}`}
                                        id="subscriptionEmail"
                                        placeholder="email@example.com"
                                        value={subscriptionEmail}
                                        onChange={(e) => setSubscriptionEmail(e.target.value)}
                                    />
                                    {emailMismatchWarning && (
                                        <div className="invalid-feedback d-block">
                                            {emailMismatchWarning}
                                        </div>
                                    )}
                                </div>

                                {/* Phone Field */}
                                <div className="mb-3">
                                    <label className="form-label" htmlFor="subscriptionPhone">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        className="form-control"
                                        id="subscriptionPhone"
                                        placeholder="+1 (555) 123-4567"
                                        value={subscriptionPhone}
                                        onChange={(e) => setSubscriptionPhone(e.target.value)}
                                    />
                                </div>

                                <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                                    * Provide at least email or phone number
                                </p>

                                {/* Consent Section Header */}
                                <div className="mb-3">
                                    <strong>I consent to receive SMS notifications from KlonIT for:</strong>
                                </div>

                                {/* Promotional Offers Consent */}
                                <div className="mb-3">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="promotionalConsent"
                                            checked={promotionalConsent}
                                            onChange={(e) => setPromotionalConsent(e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="promotionalConsent">
                                            <strong>Promotional offers</strong>
                                            <small className="text-muted d-block">
                                                Receive notifications about special promotions, discounts, and limited-time offers
                                            </small>
                                        </label>
                                    </div>
                                </div>

                                {/* Exclusive Deals Consent */}
                                <div className="mb-3">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="exclusiveDealsConsent"
                                            checked={exclusiveDealsConsent}
                                            onChange={(e) => setExclusiveDealsConsent(e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="exclusiveDealsConsent">
                                            <strong>General Information</strong>
                                            <small className="text-muted d-block">
                                                Any type of notifications including updates, newsletters, and important announcements
                                            </small>
                                        </label>
                                    </div>
                                </div>

                                {/* SMS Disclosure */}
                                <div className="mb-3">
                                    <small className="text-muted">
                                        Message frequency may vary. Message and data rates may apply. 
                                        Reply STOP to unsubscribe at any time. Reply HELP for assistance.
                                    </small>
                                </div>

                                {/* Terms Consent Checkbox */}
                                <div className="mb-3">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="termsConsent"
                                            checked={termsConsent}
                                            onChange={(e) => setTermsConsent(e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="termsConsent">
                                            I agree to the{" "}
                                            <a href="https://klonit.ai/terms-of-use" target="_blank" rel="noopener noreferrer">
                                                <strong>Terms and Conditions</strong>
                                            </a>
                                            {" "}and{" "}
                                            <a href="https://klonit.ai/privacy-policy" target="_blank" rel="noopener noreferrer">
                                                <strong>Privacy Policy</strong>
                                            </a>
                                        </label>
                                    </div>
                                </div>

                                {/* Privacy Notice */}
                                <div className="alert alert-secondary" role="alert" style={{ fontSize: '0.875rem' }}>
                                    <strong>Privacy Notice:</strong> We will never share, sell, or rent your contact information to third parties for marketing purposes.
                                </div>

                                {/* Join Button */}
                                <button
                                    type="button"
                                    className="btn btn-primary w-100"
                                    onClick={handleJoinSubscription}
                                    disabled={!isJoinEnabled() || isSubscribing}
                                >
                                    {isSubscribing ? "Joining..." : "Join"}
                                </button>

                                {/* Subscription Status Message */}
                                {subscriptionMessage && (
                                    <div className={`alert mt-3 ${subscriptionMessage.includes('✓') ? 'alert-success' : 'alert-warning'}`} role="alert">
                                        {subscriptionMessage}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Already Subscribed Message */}
                        {isSubscribed && (
                            <div className="alert alert-success mt-3" role="alert">
                                ✓ {subscriptionMessage || "You are subscribed to notifications."}
                            </div>
                        )}
                    </div>
                </div>
                <br />
                <br />
                <div id="getError" className={`alert alert-info ${message ? "" : "d-none"}`}>{message}</div>
            </div>
        </div>
    )
});

export default ProfilePage;